import React, { useMemo, useState } from 'react';
import { AdminPageTitle, AdminSearch, AdminTabs, Drawer, FilterBar, Panel, StatusBadge } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { adminContent, recordAudit, type AdminContent as ContentRow, type ContentType } from '../../data/admin';
const typeLabels: Record<ContentType, string> = {
  educational: 'Educativo',
  featured: 'Destaque',
  onboarding: 'Onboarding',
  help: 'Ajuda',
  banner: 'Banner',
  announcement: 'Aviso'
};
const statusLabels: Record<string, string> = {
  published: 'Publicado',
  draft: 'Rascunho',
  scheduled: 'Agendado'
};
const LOCALES = ['pt-BR', 'en-US'] as const;

/** Languages missing from a content item. Drafts may be incomplete. */
function missingLocales(row: ContentRow): string[] {
  return LOCALES.filter(locale => !row.languages.includes(locale));
}
const defaultText = {
  'pt-BR': 'O Life Score transforma os resultados do seu Daily Check em uma escala de bem-estar de 0 a 100. O bônus da Foto do Dia não entra no cálculo.',
  'en-US': 'Life Score translates your Daily Check results into a wellbeing scale from 0 to 100. The Photo of the Day bonus is not counted.'
};
export function AdminContentManagement() {
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<ContentRow | null>(null);
  const [ptText, setPtText] = useState('');
  const [enText, setEnText] = useState('');
  const [overrides, setOverrides] = useState<Record<string, Partial<ContentRow>>>({});
  const items = adminContent.map(item => ({
    ...item,
    ...overrides[item.id]
  }));
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return items.filter(item => {
      if (tab !== 'all' && item.type !== tab) return false;
      if (!term) return true;
      return item.title.toLowerCase().includes(term);
    });
  }, [tab, query, overrides]);
  const openItem = (row: ContentRow) => {
    setOpen(row);
    setPtText(row.languages.includes('pt-BR') ? defaultText['pt-BR'] : '');
    setEnText(row.languages.includes('en-US') ? defaultText['en-US'] : '');
  };
  const complete = ptText.trim() !== '' && enText.trim() !== '';
  const missingInDraft = [ptText.trim() ? null : 'pt-BR', enText.trim() ? null : 'en-US'].filter(Boolean) as string[];
  const save = (status: ContentRow['status']) => {
    if (!open) return;
    if (status !== 'draft' && !complete) return;
    const languages = LOCALES.filter(locale => locale === 'pt-BR' ? ptText.trim() : enText.trim()).join(' · ');
    setOverrides(prev => ({
      ...prev,
      [open.id]: {
        status,
        languages,
        updated: 'agora'
      }
    }));
    if (status !== 'draft') {
      recordAudit({
        action: status === 'published' ? {
          'pt-BR': 'Publicou conteúdo',
          'en-US': 'Published content'
        } : {
          'pt-BR': 'Agendou conteúdo',
          'en-US': 'Scheduled content'
        },
        entity: open.id,
        context: {
          'pt-BR': 'Versões pt-BR e en-US completas',
          'en-US': 'pt-BR and en-US versions complete'
        },
        before: open.status,
        after: status,
        severity: 'info'
      });
    }
    setOpen(null);
  };
  const columns: Column<ContentRow>[] = [{
    id: 'title',
    header: 'Título',
    width: '34%',
    sortValue: row => row.title,
    cell: row => <span className="min-w-0" data-id="element-1600" data-mp="3l4urn1st7na2">
          <span className="block text-[12.5px] font-medium leading-snug text-deep" data-id="element-1601" data-mp="3p4bxvxg28oga">
            {row.title}
          </span>
          <span className="block text-[11px] text-muted" data-id="element-1602" data-mp="3b5lygb5rqcor">{typeLabels[row.type]}</span>
        </span>
  }, {
    id: 'languages',
    header: 'Versões',
    cell: row => {
      const missing = missingLocales(row);
      return <span className="flex flex-wrap gap-1" data-id="element-1603" data-mp="1ebsbox3md1px">
            {LOCALES.map(locale => missing.includes(locale) ? <span key={locale} className="bee-num rounded-pill border border-dashed border-deep/40 px-2 py-[2px] text-[10.5px] font-bold text-deep" data-id="element-1604" data-mp="3ae3z3jj5n6li">
                  Falta {locale}
                </span> : <span key={locale} className="bee-num rounded-pill border border-sand-line bg-white px-2 py-[2px] text-[10.5px] font-medium text-muted" data-id="element-1605" data-mp="2wg74gso9ha9h">
                  {locale}
                </span>)}
          </span>;
    }
  }, {
    id: 'author',
    header: 'Responsável',
    sortValue: row => row.author,
    cell: row => <span className="text-[12.5px] text-muted" data-id="element-1606" data-mp="2dqh9o5ki0ohk">{row.author}</span>,
    optional: true
  }, {
    id: 'updated',
    header: 'Atualizado',
    sortValue: row => row.updated,
    cell: row => <span className="bee-num text-muted" data-id="element-1607" data-mp="23ribjgxd0b8t">{row.updated}</span>
  }, {
    id: 'status',
    header: 'Status',
    sortValue: row => row.status,
    cell: row => <StatusBadge status={row.status} label={statusLabels[row.status]} data-id="element-1608" data-mp="o6ptisjn3w4k" />
  }];
  return <div className="space-y-4" data-id="element-1609" data-mp="t7wzqn64wn94">
      <AdminPageTitle title="Conteúdo" subtitle="Conteúdo educativo, onboarding, ajuda, destaques, banners e avisos em pt-BR e en-US. Rascunhos podem ter tradução incompleta; publicar ou agendar exige as duas versões." action={<Button variant="gold" data-id="element-1611" data-mp="5grl2bbxk10e">Novo conteúdo</Button>} data-id="element-1610" data-mp="t7n6mqpslzz7" />

      <AdminTabs tabs={[{
      id: 'all',
      label: 'Tudo'
    }, {
      id: 'educational',
      label: 'Educativo'
    }, {
      id: 'featured',
      label: 'Destaques'
    }, {
      id: 'onboarding',
      label: 'Onboarding'
    }, {
      id: 'help',
      label: 'Ajuda'
    }, {
      id: 'banner',
      label: 'Banners'
    }, {
      id: 'announcement',
      label: 'Avisos'
    }]} active={tab} onChange={setTab} data-id="element-1612" data-mp="5f6s2q59kq4n" />

      <FilterBar data-id="element-1613" data-mp="p3o832uchrvp">
        <AdminSearch value={query} onChange={setQuery} placeholder="Buscar por título" data-id="element-1614" data-mp="p362vgppg3bn" />
      </FilterBar>

      <DataTable rows={rows} columns={columns} rowKey={row => row.id} onRowClick={openItem} emptyTitle="Nenhum conteúdo" emptyBody="Ajuste a busca ou crie um novo conteúdo." actions={[{
      id: 'edit',
      label: 'Abrir e editar',
      onSelect: openItem
    }]} data-id="element-1615" data-mp="k435mifyv1e8" />

      <Drawer open={open !== null} title={open?.title ?? ''} subtitle={open ? `${typeLabels[open.type]} · ${statusLabels[open.status]}` : undefined} onClose={() => setOpen(null)} footer={<>
            <Button variant="outline" onClick={() => save('draft')} data-id="element-1617" data-mp="b4g9ab23gls1">
              Salvar rascunho
            </Button>
            <Button variant="outline" disabled={!complete} onClick={() => save('scheduled')} data-id="element-1618" data-mp="1qkfaf2danhis">
              Agendar
            </Button>
            <Button variant="gold" disabled={!complete} onClick={() => save('published')} data-id="element-1619" data-mp="1mj26ixtrhoei">
              Publicar
            </Button>
          </>} data-id="element-1616" data-mp="65vg2vl9f2d3">
        <div className="space-y-3" data-id="element-1620" data-mp="1z2h8qak9xfn8">
          <Panel title="Versão pt-BR" data-id="element-1621" data-mp="1z3ilcjgxua72">
            <textarea rows={5} value={ptText} onChange={event => setPtText(event.target.value)} aria-invalid={ptText.trim() === ''} placeholder="Versão em português ainda não escrita." className="w-full rounded-card border border-sand-line bg-white p-2 text-[13px] leading-relaxed text-deep focus:border-deep/40 focus:outline-none" data-id="element-1622" data-mp="1k56nwalqqxeu" />
          </Panel>
          <Panel title="Versão en-US" data-id="element-1623" data-mp="2339jx9cphum5">
            <textarea rows={5} value={enText} onChange={event => setEnText(event.target.value)} aria-invalid={enText.trim() === ''} placeholder="English version not written yet." className="w-full rounded-card border border-sand-line bg-white p-2 text-[13px] leading-relaxed text-deep focus:border-deep/40 focus:outline-none" data-id="element-1624" data-mp="282vxjjwg5cvh" />
          </Panel>
          <Panel title="Publicação" data-id="element-1625" data-mp="vp5ry0pfx2zc">
            {missingInDraft.length > 0 ? <p role="status" className="text-[12.5px] font-medium leading-relaxed text-deep" data-id="element-1626" data-mp="rnu15rutybpk">
                Falta a versão {missingInDraft.join(' e ')}. Você pode salvar como rascunho, mas
                publicar e agendar ficam indisponíveis até as duas versões estarem completas.
              </p> : <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-1627" data-mp="1ffixrkeopmcb">
                As duas versões estão completas. Publicar ou agendar fica registrado na auditoria
                com o responsável e o horário.
              </p>}
          </Panel>
        </div>
      </Drawer>
    </div>;
}